import {
  blockedInFullstorySelector,
  inlineDisplays,
  keptTags,
  loadingIndicatorSelector,
  notReadableByOperatorSelector,
} from "./constants";
import { describeHref } from "./describeHref";
import { escapeText } from "./escapeText";
import { html } from "./html";
import { isElementVisible } from "./isElementVisible";
import type { HtmlAttributeValue, ScrapeNodeResult } from "./types";

/** The attributes Operator needs from a real page element. Described elements carry their own. */
const readAttributes = (element: HTMLElement): Record<string, HtmlAttributeValue> => {
  const attributes: Record<string, HtmlAttributeValue> = {
    id: element.getAttribute("data-operator-id") ?? undefined,
    "aria-label": element.getAttribute("aria-label") ?? undefined,
    disabled: "disabled" in element && element.disabled === true,
  };
  if (element instanceof HTMLAnchorElement) {
    attributes.href = describeHref(element);
  }
  if (element instanceof HTMLInputElement) {
    const isToggle = element.type === "checkbox" || element.type === "radio";
    attributes.type = element.type === "text" ? undefined : element.type;
    attributes.placeholder = element.placeholder;
    attributes.checked = element.checked;
    // A toggle's value is a form detail ("on" by default); its checked state is what the user sees.
    attributes.value = element.type === "password" || isToggle ? undefined : element.value.trim();
  }
  if (element instanceof HTMLTextAreaElement) {
    attributes.placeholder = element.placeholder;
  }
  if (element instanceof HTMLIFrameElement) {
    attributes.title = element.title || element.getAttribute("aria-label") || element.name || undefined;
    attributes["aria-label"] = undefined;
  }
  if (element instanceof HTMLImageElement) {
    attributes.alt = element.alt;
  }
  return attributes;
};

/** A described element sits on its own line; for anything else the browser's layout decides. */
const isBlock = (node: Node): boolean =>
  node instanceof HTMLElement &&
  (node.hasAttribute("data-operator-description") || !inlineDisplays.has(window.getComputedStyle(node).display));

/**
 * Serializes what the user sees in `root` as a stripped HTML clone: visible text, the tags in
 * `keptTags` with the attributes from `readAttributes`, and `data-operator-description` markup
 * spliced in verbatim in place of the element it describes. Layout wrappers disappear; a block
 * boundary becomes a newline and an inline boundary a space, as when copying text from a page.
 *
 * Descriptions are first-party HTML and are never re-parsed or escaped, so JSON payloads and
 * nonce-wrapped editor content inside them stay byte-exact. Page text is always escaped, so
 * nothing a user typed can read as markup.
 */
export const scrapeNode = (root: Node | null, shouldSkipBlockedInFullstory: boolean): ScrapeNodeResult => {
  let loading = false;

  const scrapeChildren = (element: HTMLElement, preserveWhitespace: boolean): string => {
    if (preserveWhitespace) {
      return Array.from(element.childNodes)
        .map((child) => (child instanceof HTMLBRElement ? "\n" : scrape(child, true)))
        .join("");
    }

    let text = "";
    let previousWasBlock = false;
    for (const child of element.childNodes) {
      // A line break has no text of its own but is a block boundary for its neighbours.
      if (child instanceof HTMLBRElement) {
        previousWasBlock = true;
        continue;
      }
      const childText = scrape(child, false);
      if (!childText) {
        continue;
      }
      const block = isBlock(child);
      if (text) {
        text += block || previousWasBlock ? "\n" : " ";
      }
      text += childText;
      previousWasBlock = block;
    }
    return text;
  };

  /** `preserveWhitespace` is set inside `<pre>`, where newlines and indentation are content. */
  const scrape = (node: Node | null, preserveWhitespace: boolean): string => {
    if (node instanceof Text) {
      const text = node.textContent || "";
      return escapeText(preserveWhitespace ? text : text.replace(/\s+/g, " ").trim());
    }
    if (!(node instanceof HTMLElement) || !isElementVisible(node)) {
      return "";
    }
    if (node.matches(notReadableByOperatorSelector)) {
      return "";
    }
    if (shouldSkipBlockedInFullstory && node.matches(blockedInFullstorySelector)) {
      return "";
    }
    loading ||= node.matches(loadingIndicatorSelector);

    const tag = node.tagName.toLowerCase();

    if (node.hasAttribute("data-operator-redacted")) {
      // Only the tag survives: an href, placeholder or checked state would be the value by another name.
      return html(keptTags.has(tag) ? tag : "span", { "data-operator-redacted": true });
    }
    if (node.hasAttribute("data-operator-description")) {
      const description = node.getAttribute("data-operator-description") || "";
      if (!node.hasAttribute("data-operator-include-children")) {
        return description;
      }
      return [description, scrapeChildren(node, preserveWhitespace)].filter(Boolean).join("\n");
    }
    if (node instanceof HTMLTextAreaElement) {
      return html(tag, readAttributes(node), escapeText(node.value.trim()));
    }
    if (node instanceof HTMLSelectElement) {
      const selected = node.options[node.selectedIndex];
      return html(
        tag,
        readAttributes(node),
        selected ? html("option", { selected: true }, escapeText(selected.text.trim())) : ""
      );
    }
    if (node instanceof HTMLInputElement || node instanceof HTMLImageElement || node instanceof HTMLIFrameElement) {
      return html(tag, readAttributes(node));
    }

    const children = scrapeChildren(node, preserveWhitespace || tag === "pre");
    if (!keptTags.has(tag)) {
      return children || escapeText(node.getAttribute("aria-label") || "");
    }
    return html(tag, readAttributes(node), children);
  };

  return { content: scrape(root, false), loading };
};
